import React from 'react';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import classnames from 'classnames';
import Compressor from 'compressorjs';

import CRMLocationField from '@containers/crm/CRMIssueFormFields/CRMLocationField';

import Accordion from '@components/Accordion';
import { AttachmentImage } from '@components/Attachment';
import { ACCEPTED_FILE_TYPES } from '@components/Attachment/AttachmentsModal';
import Button from '@components/Button';
import MultiSectionDropdown, { CategoryPickerSelection } from '@components/DropdownMultiSection/MultiSectionDropdown';
import DuplicateCheckLoader from '@components/DuplicateCheckLoader/DuplicateCheckLoader';
import DuplicateDetectionModal from '@components/DuplicateDetectionModal';
import Input from '@components/Input';
import Loading from '@components/Loading';

import { AuthManagerContext, AuthManagerContextValue } from '@private-crm-app/auth-manager-context';
import PrivateSubmissionLayout from '@private-crm-app/components/PrivateSubmissionLayout/PrivateSubmissionLayout';
import {
  CRM_ISSUE_ID,
  PENDING_FOLLOW_ISSUE_IDS,
  PRIVATE_SUBMISSION_LISTING_TAB_FOLLOWING_VALUE,
  PRIVATE_SUBMISSION_LISTING_TAB_QUERY_KEY,
  PrivateSubmissionAppRoutes,
  SUBMISSION_DRAFT_KEY,
  SUBMISSION_REQUEST_DATA,
  SUBMIT_ANONYMOUSLY,
} from '@private-crm-app/constants';
import { matchCategoryByName } from '@private-crm-app/matchCategoryByName';
import PrivateSubmissionDetails from '@private-crm-app/PrivateSubmissionDetailPage/PrivateSubmissionDetails';
import { followPublicMapIssue, unfollowPublicMapIssue } from '@private-crm-app/requests/publicMap';
import { UserContext, UserContextValue } from '@private-crm-app/user-context';
import {
  buildIssueUrl,
  getCRMToken,
  safeParseJson,
  trackCategorySearchNoResults,
  trackCategorySelected,
  trackPublicMapDuplicateSuggestionShown,
  trackPublicMapFollowClickedDuplicateSkip,
  trackRequestCreated,
  trackRequestStarted,
  trackRequestSubmitted,
  trackSubmitAnonymous,
} from '@private-crm-app/utils';

import { addError } from '@actions/appState';
import {
  clearIssue,
  GET_DUPLICATE_ISSUES_SUCCESS,
  getCRMAnonymousIssueDetails,
  getCRMMapConfig,
  getCRMSuggestedCategories,
  getDuplicateIssues,
  saveCRMAnonymousIssueDetails,
  SUBMIT_ISSUE_SUCCESS,
  submitIssue,
  validateCRMToken,
} from '@actions/crm';
import { getSSOConfig, getSSOToken, getSSOUserInfo } from '@actions/crmSso';
import { getCRMSignedURL, S3_CRM_SIGNED_URL_SUCCESS } from '@actions/s3';

import {
  getEmptyErrorState,
  getFileType,
  getIndexedFileName,
  getS3URL,
  humanFileSize,
  isDesktop,
  MAX_ATTACHMENT_FILE_SIZE,
} from '@utils/AppUtil';
import CustomFieldUtil from '@utils/CustomFieldUtil';
import CyberdyneValidator from '@utils/cyberdyneValidator';
import ErrorMessageGenerator from '@utils/ErrorMessageGenerator';
import { isAccountsFeatureFlagEnabled } from '@utils/FeatureFlagUtil';
import FileUtil from '@utils/FileUtil';
import LocationUtil from '@utils/LocationUtil';
import { s3uploadFile } from '@utils/S3Util';

import './style.scss';

export interface ICRMIssueFormProps extends RouteComponentProps {
  // From redux
  issue: ICRMIssueSubmissionResponse;
  categories: ICRMCategoryResponse[];
  validateCRMToken: TValidateCRMToken;
  saveCRMAnonymousIssueDetails: TSaveCRMAnonymousIssueDetails;
  getCRMAnonymousIssueDetails: TGetCRMAnonymousIssueDetails;
  anonymousSubmission?: ICRMIssueAnonymousDetails;
  submitIssue: TSubmitIssue;
  s3Operation?: S3Operation;
  acceptedFileTypes?: string[];
  maxFileSize?: number;
  isSMSEnabled: boolean;
  postAnonymous: boolean;
  getCRMSignedURL: TGetCRMSignedURL;
  addError: TAddError;
  getCRMConfig: TGetCRMMapConfig;
  getSuggestedCategories: TGetCRMSuggestedCategory;
  contactPreferenceNote: string;
  getSsoToken: TGetIDPToken;
  getSsoConfig: TGetSsoConfig;
  getSsoUserInfo: TGetUserInfo;
  gisVersion?: GISVersion;
  clearIssue: TClearIssue;
  isAccountsFeatureFlagEnabled: boolean;
  presentDuplicateSubmissionsToCitizens: boolean;
  getDuplicateIssues: (
    crmToken: string,
    params: ICRMDuplicateIssueRequest
  ) => Promise<{ type: string; response?: ICRMDuplicateIssueResult[] }>;
}

const enum PrivateSubmissionFormSteps {
  CategoryDetails = 0,
  LocationDetails = 1,
}

export interface ICRMIssueFormState {
  loading: boolean;
  isCheckingDuplicates: boolean;
  markerCenter: IMapCoordinate;
  activeStep: PrivateSubmissionFormSteps;
  categoryFilled: boolean;
  description: string;
  suggestedCategories: number[];
  error: {
    notificationNote: IFieldValidation;
    description: IFieldValidation;
    firstName: IFieldValidation;
    lastName: IFieldValidation;
    email: IFieldValidation;
    location: IFieldValidation;
    phoneNumber: IFieldValidation;
    workPhone: IFieldValidation;
  };
  files: Map<string, IFileUpload>;
  location: ILocation;
  category: ICRMCategoryResponse;
  fieldValues: IFieldState[];
  categoryfieldValues: IFieldValues;
  isAnonymous: boolean;
  showSuccessToast: boolean;
  isDuplicateModalOpen: boolean;
  duplicateCandidates: ICRMDuplicateIssueResult[];
}

const errMsgGen = new ErrorMessageGenerator();
const cyberdyneValidator = new CyberdyneValidator();
const locationUtil = new LocationUtil();
const customFieldUtil = new CustomFieldUtil();

const SUCCESS_TOAST_TIMEOUT = 5000;
const ATTACHMENT_UPLOAD_FAILED_MESSAGE =
  'The attachment did not attach. You can retry the upload or submit without it.';
const SUBMIT_REQUEST_FAILED_MESSAGE = 'Failed to submit request. Please try again.';

// Create a HOC to wrap the component with context
const withUserAndAuthContext = (Component: React.ComponentType<any>) =>
  function ContextComponent(props: any) {
    return (
      <AuthManagerContext.Consumer>
        {(authContext) => (
          <UserContext.Consumer>
            {(userContext) => <Component {...props} userContext={userContext} authContext={authContext} />}
          </UserContext.Consumer>
        )}
      </AuthManagerContext.Consumer>
    );
  };

export class PrivateSubmissionForm extends React.Component<
  ICRMIssueFormProps & { userContext: UserContextValue; authContext: AuthManagerContextValue },
  ICRMIssueFormState
> {
  static defaultProps: Partial<ICRMIssueFormProps> = {
    acceptedFileTypes: ACCEPTED_FILE_TYPES,
    maxFileSize: MAX_ATTACHMENT_FILE_SIZE,
    s3Operation: S3Operation.CRM_ISSUE_ATTACHMENT,
  };

  static contextType = UserContext;
  declare context: React.ContextType<typeof UserContext>;

  private autosaveTimeout: NodeJS.Timeout | null = null;

  private categoryQueryApplied = false;

  private categorySelectionCount = 0;

  constructor(props: ICRMIssueFormProps & { userContext: UserContextValue; authContext: AuthManagerContextValue }) {
    super(props);

    this.state = this.getEmptyState();
  }

  // Contact creation is now handled exclusively in user-context.tsx

  // Contact creation is now handled exclusively in user-context.tsx
  // This prevents race conditions and duplicate contact creation
  ensureUserContact = async () => {
    console.log('[CONTACT_DEBUG] PrivateSubmissionForm - Contact creation delegated to user-context.tsx');
    // Simply return the existing user from context, no contact creation here
    return this.props.userContext?.user || null;
  };

  componentDidMount() {
    const crmToken = getCRMToken();

    if (crmToken && !this.props.categories?.length) {
      this.props.getCRMConfig(crmToken);

      const { name, email } = this.props.userContext?.user ?? {};

      const { isAuthenticated } = this.props.authContext;

      trackRequestStarted(name, email, isAuthenticated);
    }

    this.createIssueOnRedirectFromAuth0();

    // Contact creation is now handled exclusively in user-context.tsx
    // No need to call ensureUserContactOnMount here

    // Load draft data if exists and we're not in the middle of a submission
    if (this.hasSubmissionRequestData()) {
      this.categoryQueryApplied = true;
    } else {
      const draftData = this.loadDraftData();
      const applyCategory = () => this.applyCategoryFromQuery();
      if (draftData) {
        this.setState(
          {
            ...this.getEmptyState(), // Start with empty state
            ...draftData, // Override with draft data
            loading: false,
          },
          applyCategory
        );
      } else {
        this.setState({ loading: false }, applyCategory);
      }
    }
  }

  private applyCategoryFromQuery = () => {
    if (this.categoryQueryApplied || !this.props.categories?.length) {
      return;
    }

    if (this.hasSubmissionRequestData()) {
      this.categoryQueryApplied = true;

      return;
    }

    const categoryParam = new URLSearchParams(this.props.location?.search || '').get('category');
    this.categoryQueryApplied = true;

    if (!categoryParam?.trim()) {
      return;
    }

    const match = matchCategoryByName(this.props.categories, categoryParam);
    if (match) {
      this.handleCategorySelection(match)();
    }
  };

  // Contact creation is now handled exclusively in user-context.tsx
  // No need for ensureUserContactOnMount wrapper

  componentDidUpdate(
    prevProps: ICRMIssueFormProps & { userContext: UserContextValue; authContext: AuthManagerContextValue },
    prevState: ICRMIssueFormState
  ) {
    this.applyCategoryFromQuery();

    // Autosave when form data changes
    if (this.shouldAutosave(prevState)) {
      this.scheduleAutosave();
    }

    // Handle post-onboarding submission redirect
    this.handlePostOnboardingRedirect(prevProps);

    // Contact creation is now handled exclusively in user-context.tsx
    // No need to check auth state changes here
  }

  private handlePostOnboardingRedirect(
    prevProps: ICRMIssueFormProps & { userContext: UserContextValue; authContext: AuthManagerContextValue }
  ) {
    const prevUser = prevProps.userContext?.user;
    const currentUser = this.props.userContext?.user;

    // Early exit if no user data or users are different
    if (!prevUser || !currentUser || prevUser.id !== currentUser.id) {
      return;
    }

    // Check if onboarding status changed from false to true (completed)
    const onboardingCompleted = prevUser.isOnboardingDone === false && currentUser.isOnboardingDone === true;

    // Check if we have stored submission data indicating a successful submission waiting for redirect
    const hasStoredSubmissionId = localStorage.getItem(CRM_ISSUE_ID);
    const hasToastMarker =
      hasStoredSubmissionId && sessionStorage.getItem(`submission_created_toast_${hasStoredSubmissionId}`) === '1';

    if (onboardingCompleted && hasToastMarker && !this.state.loading) {
      // User completed onboarding and we have a submission waiting - redirect to details page
      this.redirectToDetailsPage(hasStoredSubmissionId);
    }
  }

  componentWillUnmount(): void {
    if (this.props.issue) {
      this.props.clearIssue();
    }

    // Clear any pending autosave
    if (this.autosaveTimeout) {
      clearTimeout(this.autosaveTimeout);
    }
  }

  private shouldAutosave(prevState: ICRMIssueFormState): boolean {
    // Only autosave if relevant form fields have changed
    return (
      prevState.description !== this.state.description ||
      prevState.location !== this.state.location ||
      prevState.category !== this.state.category ||
      prevState.fieldValues !== this.state.fieldValues ||
      prevState.categoryfieldValues !== this.state.categoryfieldValues ||
      prevState.isAnonymous !== this.state.isAnonymous
    );
  }

  private scheduleAutosave() {
    // Clear any existing timeout
    if (this.autosaveTimeout) {
      clearTimeout(this.autosaveTimeout);
    }

    // Schedule new autosave after 1 second of no changes
    this.autosaveTimeout = setTimeout(() => {
      this.saveDraftData();
    }, 1000);
  }

  private saveDraftData() {
    if (this.hasSubmissionRequestData()) {
      return;
    }

    const draftData = {
      description: this.state.description,
      location: this.state.location,
      category: this.state.category,
      fieldValues: this.state.fieldValues,
      categoryfieldValues: this.state.categoryfieldValues,
      isAnonymous: this.state.isAnonymous,
    };
    localStorage.setItem(SUBMISSION_DRAFT_KEY, JSON.stringify(draftData));
  }

  private loadDraftData(): Partial<ICRMIssueFormState> | null {
    const draftData = localStorage.getItem(SUBMISSION_DRAFT_KEY);

    return draftData ? JSON.parse(draftData) : null;
  }

  private purgeSubmissionDraft(): void {
    if (this.autosaveTimeout) {
      clearTimeout(this.autosaveTimeout);
      this.autosaveTimeout = null;
    }
    localStorage.removeItem(SUBMISSION_DRAFT_KEY);
  }

  private storeSubmissionSuccess(submissionId: string | number) {
    const id = String(submissionId);
    localStorage.setItem(CRM_ISSUE_ID, id);
    sessionStorage.setItem(`submission_created_toast_${id}`, '1');
  }

  private completeSubmissionSuccess(submissionId: string | number): void {
    this.purgeSubmissionDraft();
    this.storeSubmissionSuccess(submissionId);
    localStorage.removeItem(SUBMISSION_REQUEST_DATA);
    this.categorySelectionCount = 0;

    const { isAuthenticated } = this.props.authContext;
    const user = this.props.userContext?.user;

    if (isAuthenticated && user && user.isOnboardingDone === false) {
      this.setState({ ...this.getEmptyState(), loading: false });

      return;
    }

    if (isAuthenticated) {
      this.redirectToDetailsPage(submissionId);
    } else {
      this.showToastOnForm();
    }
  }

  private showToastOnForm() {
    this.setState({
      ...this.getEmptyState(),
      loading: false,
      showSuccessToast: true,
    });

    setTimeout(() => {
      this.setState({ showSuccessToast: false });
    }, SUCCESS_TOAST_TIMEOUT);
  }

  private redirectToDetailsPage(crmIssueId: string | number) {
    this.setState({ ...this.getEmptyState(), loading: false });
    this.props.history.push(buildIssueUrl(crmIssueId, getCRMToken()));
  }

  getEmptyState = (): ICRMIssueFormState => ({
    loading: this.hasSubmissionRequestData(),
    isCheckingDuplicates: false,
    markerCenter: null,
    description: '',
    categoryFilled: true,
    suggestedCategories: [],
    activeStep: PrivateSubmissionFormSteps.CategoryDetails,
    error: {
      notificationNote: getEmptyErrorState(),
      description: getEmptyErrorState(),
      firstName: getEmptyErrorState(),
      lastName: getEmptyErrorState(),
      email: getEmptyErrorState(),
      location: getEmptyErrorState(),
      phoneNumber: getEmptyErrorState(),
      workPhone: getEmptyErrorState(),
    },
    files: new Map(),
    location: null,
    category: null,
    fieldValues: [],
    categoryfieldValues: {},
    isAnonymous: false,
    showSuccessToast: false,
    isDuplicateModalOpen: false,
    duplicateCandidates: [],
  });

  getSubmissionRequestData = () => JSON.parse(localStorage.getItem(SUBMISSION_REQUEST_DATA) ?? '{}');

  hasSubmissionRequestData = () => {
    const submissionRequestData = this.getSubmissionRequestData();

    return Object.keys(submissionRequestData).length > 0;
  };

  createIssueOnRedirectFromAuth0 = async () => {
    const pendingFollowIdsRaw = localStorage.getItem(PENDING_FOLLOW_ISSUE_IDS);
    if (pendingFollowIdsRaw) {
      const isUserReady = !!this.props.userContext?.user;
      const isAuthReady =
        !!this.props.authContext?.isAuthenticated &&
        !this.props.authContext?.isLoading &&
        !this.props.userContext?.isLoading;

      if (isUserReady || isAuthReady) {
        localStorage.removeItem(PENDING_FOLLOW_ISSUE_IDS);
        const pendingFollowIds = safeParseJson<number[]>(pendingFollowIdsRaw);
        if (!pendingFollowIds?.length) {
          this.setState({ ...this.getEmptyState(), loading: false });

          return;
        }
        this.setState({ loading: true });
        await this.executeFollowAndSkip(pendingFollowIds);

        return;
      }

      localStorage.removeItem(PENDING_FOLLOW_ISSUE_IDS);
      this.setState({ ...this.getEmptyState(), loading: false });

      return;
    }

    const hasSubmissionRequestData = this.hasSubmissionRequestData();

    // if user click on submit anonymously on auth0 page, we don't need to get user info
    const urlParams = new URLSearchParams(window.location.search);
    const submitAnonymously = urlParams.get(SUBMIT_ANONYMOUSLY) === 'true';

    if (hasSubmissionRequestData) {
      this.setState({ loading: true });

      const submissionRequestData = this.getSubmissionRequestData();

      if (submitAnonymously) {
        trackSubmitAnonymous(submissionRequestData.category.name);

        submissionRequestData.isAnonymous = true;

        this.props
          .submitIssue(getCRMToken(), submissionRequestData)
          .then((action) => {
            if (action.type === SUBMIT_ISSUE_SUCCESS) {
              this.completeSubmissionSuccess(action.response.id);
            } else {
              localStorage.removeItem(SUBMISSION_REQUEST_DATA);
              this.setState({ ...this.getEmptyState(), loading: false });
            }
            trackRequestCreated(submissionRequestData.category.name);
          })
          .catch(() => {
            localStorage.removeItem(SUBMISSION_REQUEST_DATA);
            this.setState({ ...this.getEmptyState(), loading: false });
          });
        // Keep loading state active until submission is complete
      } else if (this.props.userContext?.user) {
        const { name, email, cellPhone, workPhone, notificationNote, notificationPreference } =
          this.props.userContext.user || {};

        // Get Auth0 user ID from auth context
        const auth0UserId = this.props.userContext?.auth0User?.sub;
        // Get the contactId from user context
        const contactId = this.props.userContext?.user?.id;

        const customerInfo = {
          firstName: name || '',
          lastName: '',
          hasSubscribed: true,
          email: email || null,
          phoneNumber: cellPhone ? cellPhone.replace(/-/g, '') : null,
          workPhone: workPhone ? workPhone.replace(/-/g, '') : null,
          notificationPreference: notificationPreference || [],
          notificationNote: notificationNote || null,
          auth0UserId,
          contactId,
        };

        submissionRequestData.customerInfo = customerInfo;
        submissionRequestData.userId = auth0UserId;
        submissionRequestData.contactId = contactId;

        // Keep loading state active until submission is complete
        this.props
          .submitIssue(getCRMToken(), submissionRequestData)
          .then((action) => {
            if (action.type === SUBMIT_ISSUE_SUCCESS) {
              this.completeSubmissionSuccess(action.response.id);
            } else {
              localStorage.removeItem(SUBMISSION_REQUEST_DATA);
              this.setState({ ...this.getEmptyState(), loading: false });
              this.props.addError(SUBMIT_REQUEST_FAILED_MESSAGE);
            }

            trackRequestCreated(
              submissionRequestData.category.name,
              this.props.userContext?.user?.name,
              this.props.userContext?.user?.email,
              true
            );
          })
          .catch((error) => {
            localStorage.removeItem(SUBMISSION_REQUEST_DATA);
            this.setState({ ...this.getEmptyState(), loading: false });
            this.props.addError(SUBMIT_REQUEST_FAILED_MESSAGE);
            console.log('Error submitting issue:', error);
          });
      } else if (
        this.props.userContext?.auth0User &&
        !this.props.userContext.isLoading &&
        !this.props.authContext.isLoading
      ) {
        // Handle new users who completed Auth0 signup but don't have a complete user profile yet
        const auth0UserId = this.props.userContext?.auth0User?.sub;
        const auth0UserEmail = this.props.userContext?.auth0User?.email;
        // Get the contactId from user context if available
        let contactId = this.props.userContext?.user?.id;

        if (auth0UserId && auth0UserEmail) {
          // Contact creation is now handled exclusively in user-context.tsx
          // Use the contact ID from user context
          contactId = this.props.userContext?.user?.id || contactId;

          // Create minimal customer info from database or Auth0 data
          // Use updated user data after potential contact creation and refresh
          const user = this.props.userContext?.user;

          const customerInfo = {
            firstName: user?.name?.split(' ')[0] || auth0UserEmail.split('@')[0],
            lastName: user?.name?.split(' ').length > 1 ? user?.name?.split(' ').slice(1).join(' ') : '',
            hasSubscribed: true,
            email: user?.email || auth0UserEmail,
            phoneNumber: user?.cellPhone || null,
            workPhone: user?.workPhone || null,
            notificationPreference: user?.notificationPreference || ['EMAIL'],
            notificationNote: user?.notificationNote || null,
            auth0UserId,
            contactId: user?.id || contactId,
          };

          submissionRequestData.customerInfo = customerInfo;
          submissionRequestData.userId = auth0UserId;
          submissionRequestData.contactId = user?.id || contactId;

          // Keep loading state active until submission is complete
          this.props
            .submitIssue(getCRMToken(), submissionRequestData)
            .then((action) => {
              if (action.type === SUBMIT_ISSUE_SUCCESS) {
                this.completeSubmissionSuccess(action.response.id);
              } else {
                localStorage.removeItem(SUBMISSION_REQUEST_DATA);
                this.setState({ ...this.getEmptyState(), loading: false });
                this.props.addError(SUBMIT_REQUEST_FAILED_MESSAGE);
              }
              trackRequestCreated(
                submissionRequestData.category.name,
                customerInfo.firstName,
                customerInfo.email,
                true
              );
            })
            .catch((error) => {
              localStorage.removeItem(SUBMISSION_REQUEST_DATA);
              this.setState({ ...this.getEmptyState(), loading: false });
              this.props.addError(SUBMIT_REQUEST_FAILED_MESSAGE);
              console.error('Error submitting issue:', error);
            });
        } else {
          // Missing required Auth0 information
          this.setState({ ...this.getEmptyState(), loading: false });
          localStorage.removeItem(SUBMISSION_REQUEST_DATA);
          this.props.addError('Authentication information incomplete. Please try again.');
        }
      } else if (!this.props.userContext.isLoading && !this.props.authContext.isLoading) {
        // if user is not authenticated (e.g. user clicks on back button from auth0 page), we need to set loading to false
        this.setState({ ...this.getEmptyState(), loading: false });
        localStorage.removeItem(SUBMISSION_REQUEST_DATA);
      }
    } else {
      this.setState({ loading: false });
    }
  };

  handleChange = (e?: React.SyntheticEvent<any>) => {
    const { name, value } = e.currentTarget;
    this.setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  removeFile = (fileKey: string) => {
    const currentFiles = this.state.files;
    const newFiles = new Map(currentFiles);
    newFiles.delete(fileKey);

    this.setState({
      files: newFiles,
    });
  };

  getEmptyFieldValues = (categoryId: number | string): IFieldValues => {
    const { categories } = this.props;
    const category = categories ? categories.find((item) => item.id == categoryId) : null;
    const fieldValues =
      category && category.fields
        ? category.fields.reduce((acc, curr: any) => {
            const id = curr.hasOwnProperty('_id') ? curr._id : curr.id;
            if (curr.isActive && !curr.isDeleted) {
              acc[id] = {
                id,
                isRequired: curr.isRequired,
                label: curr.label,
                type: curr.type,
                value: '',
                error: getEmptyErrorState(),
              };
            }

            return acc;
          }, {})
        : {};

    return fieldValues;
  };

  private syncCategoryQuery = (category: ICRMCategoryResponse) => {
    const queryParams = new URLSearchParams(this.props.location.search);
    if (!queryParams.has('category')) {
      return;
    }

    queryParams.set('category', category.name);
    this.props.history.replace({
      ...this.props.location,
      search: `?${queryParams.toString()}`,
    });
  };

  private handleCategoryPickerSelect = (selection: CategoryPickerSelection) => {
    this.categorySelectionCount += 1;
    trackCategorySelected(this.props.authContext.isAuthenticated, {
      selectionPath: selection.selectionPath,
      position: selection.position,
      searchTerm: selection.searchTerm,
      nameMatchCount: selection.nameMatchCount,
      suggestedCount: selection.suggestedCount,
      category: selection.category,
      selectionNumber: this.categorySelectionCount,
    });
  };

  private handleCategorySearchNoResults = (searchTerm: string) => {
    trackCategorySearchNoResults(this.props.authContext.isAuthenticated, searchTerm);
  };

  handleCategorySelection = (category: ICRMCategoryResponse) => (e?: React.SyntheticEvent<any>) => {
    const fieldValues = customFieldUtil.getDefaultFieldValuesState(category.fields, null, true);
    let categoryfieldValues = this.state.categoryfieldValues;
    categoryfieldValues = this.getEmptyFieldValues(+category?.id);
    this.syncCategoryQuery(category);

    // Check if the selected category allows anonymous submissions
    const newState: Partial<ICRMIssueFormState> = {
      categoryFilled: true,
      category,
      fieldValues,
      error: this.getEmptyState().error,
      categoryfieldValues,
    };

    // Force isAnonymous to false if category doesn't allow anonymous submissions
    if (category && category.allowAnonymousSubmission === false) {
      newState.isAnonymous = false;
    }

    this.setState((prevState) => ({
      ...prevState,
      ...newState,
    }));
  };

  getEmptyAttributes = () => {
    const res = [];
    const lookup = {
      city: 'City',
      state: 'State',
      zip: 'Zip',
    };

    for (const attr in lookup) {
      if (this.state.location && !this.state.location[attr]) {
        res.push(lookup[attr]);
      }
    }

    return res.join(', ');
  };

  validateDescriptionForm = () => {
    let isValid = true;
    const { description, category: selectedCategory } = this.state;
    const error = this.getEmptyState().error;
    if (cyberdyneValidator.isEmptyString(description)) {
      error.description = {
        errorState: IValidationState.ERROR,
        errorMessage: errMsgGen.getMandatoryFieldMessage('Description'),
      };

      isValid = false;
    } else if (cyberdyneValidator.isStringGreaterThanLength(description.trim(), 2000)) {
      error.description = {
        errorState: IValidationState.ERROR,
        errorMessage: errMsgGen.getCharacterLimitMessage('Issue description', 2000),
      };

      isValid = false;
    }

    if (this.isAddDisabled()) {
      isValid = false;
    }

    if (selectedCategory === null) {
      this.setState({ categoryFilled: false });
    }

    if (!this.validateCategoryCustomFieldForm()) {
      isValid = false;
    }
    this.setState({ error });

    return isValid;
  };

  validateLocationForm = () => {
    let isValid = true;
    const {
      location,
      category,
      error: { location: locationError },
    } = this.state;
    const error = this.getEmptyState().error;
    if (category && category.isLocationIncluded) {
      if (!location && category.isLocationRequired) {
        error.location.errorState = IValidationState.ERROR;
        error.location.errorMessage = errMsgGen.getMandatoryFieldMessage('location');
        isValid = false;
      }
    }

    if (location) {
      const missingAttr = this.getEmptyAttributes();
      if (missingAttr) {
        error.location.errorState = IValidationState.ERROR;
        error.location.errorMessage = `Missing mandatory location field(s) - ${missingAttr}`; // tslint:disable-line
        isValid = false;
      }

      if (!!locationError.errorMessage) {
        isValid = false;
        error.location = locationError;
      }
    }

    this.setState({ error });

    return isValid;
  };

  getIssueSubmissionRequest = (isAuthenticated = false) => {
    if (this.isAddDisabled()) {
      return null;
    }
    let location = null;
    let customerInfo = null;
    // Get the contactId from user context if available
    const contactId = this.props.userContext?.user?.id;

    const { files, categoryfieldValues } = this.state;

    const crmAttachmentFiles: ICRMIssueAttachment[] = Array.from(files.keys())
      .map((fileKey) => {
        const file = files.get(fileKey);

        return {
          fileName: fileKey,
          fileSize: humanFileSize(file.details.size),
          fileURL: file.s3SavedUrl,
        };
      })
      .filter((attachment) => !!attachment.fileURL);

    if (this.state.location) {
      const { lat, long, streetAddress, placeName, ...rest } = this.state.location;

      location = {
        latitude: lat,
        longitude: long,
        locationSource: LocationSource.CITIZEN_SUBMISSION,
        streetAddress: streetAddress || null,
        placeName: placeName || null,
        ...rest,
      };
    }

    // Get Auth0 user ID and email from context
    const auth0UserId = this.props.userContext?.auth0User?.sub;
    const auth0UserEmail = this.props.userContext?.auth0User?.email;

    // Always include customerInfo if we have auth0UserId
    if (auth0UserId) {
      if (this.props.userContext?.user) {
        // If user profile is available, use that info
        const {
          name,
          email,
          cellPhone,
          workPhone,
          notificationPreference,
          notificationNote,
          city,
          state,
          streetAddress,
          zip,
        } = this.props.userContext.user;

        customerInfo = {
          firstName: name,
          lastName: '',
          hasSubscribed: true,
          email: email || auth0UserEmail || null,
          phoneNumber: cellPhone ? cellPhone.replace(/-/g, '') : null,
          workPhone: workPhone ? workPhone.replace(/-/g, '') : null,
          notificationPreference,
          notificationNote: notificationNote || null,
          city,
          state,
          streetAddress,
          zip,
          auth0UserId,
          contactId,
        };
      } else if (auth0UserEmail) {
        // Minimal contact info when only auth0 data is available
        customerInfo = {
          firstName: auth0UserEmail.split('@')[0],
          lastName: '',
          hasSubscribed: true,
          email: auth0UserEmail,
          phoneNumber: null,
          workPhone: null,
          notificationPreference: ['EMAIL'],
          auth0UserId,
          contactId,
        };
      }
    }

    return {
      userId: auth0UserId,
      // Include contactId at the top level if available to use the existing contact
      ...(contactId ? { contactId } : {}),
      description: this.state.description,
      isAnonymous: this.state.isAnonymous,
      attachments: crmAttachmentFiles,
      category: {
        id: this.state.category.id,
        fields: Object.keys(categoryfieldValues).map((fieldValue) => categoryfieldValues[fieldValue]),
      },
      ...(location ? { location } : {}),
      ...(customerInfo ? { customerInfo } : {}),
    };
  };

  handleSubmitWithDuplicateCheck = async () => {
    if (this.isAddDisabled()) {
      const blockerMessage = this.getAttachmentUploadBlockerMessage();
      if (blockerMessage) {
        this.props.addError(blockerMessage);
      }

      return;
    }

    const { presentDuplicateSubmissionsToCitizens, getDuplicateIssues: fetchDuplicates } = this.props;
    const { location, category } = this.state;
    const crmToken = getCRMToken();

    const hasCoordinates = location?.lat != null && location?.long != null && !!category?.id;
    const hasDescription = !!this.state.description?.trim();

    if (!presentDuplicateSubmissionsToCitizens || !hasCoordinates || !hasDescription || !crmToken) {
      this.handleSubmit();

      return;
    }

    this.setState({ isCheckingDuplicates: true });

    try {
      const result = await fetchDuplicates(crmToken, {
        description: this.state.description.trim(),
        categoryId: category.id,
        latitude: location.lat,
        longitude: location.long,
      });

      this.setState({ isCheckingDuplicates: false });

      if (result.type === GET_DUPLICATE_ISSUES_SUCCESS && result.response?.length) {
        trackPublicMapDuplicateSuggestionShown(
          this.props.authContext.isAuthenticated,
          result.response.length,
          this.state.category?.name ?? ''
        );
        this.setState({
          isDuplicateModalOpen: true,
          duplicateCandidates: result.response,
        });
      } else {
        this.handleSubmit();
      }
    } catch {
      this.setState({ isCheckingDuplicates: false });
      this.handleSubmit();
    }
  };

  handleDuplicateModalClose = () => {
    this.setState({ isDuplicateModalOpen: false });
  };

  handleDuplicateSubmitAnyway = () => {
    this.setState({ isDuplicateModalOpen: false });
    this.handleSubmit();
  };

  private executeFollowAndSkip = async (duplicateIds: number[]): Promise<void> => {
    const crmToken = getCRMToken();
    if (!crmToken) {
      return;
    }

    let hasError = false;

    for (const id of duplicateIds) {
      try {
        await followPublicMapIssue(crmToken, id);
      } catch (err) {
        console.error('[PrivateSubmissionForm] Failed to follow duplicate', id, err);
        hasError = true;
      }
    }

    if (hasError) {
      this.props.addError(
        'Some submissions could not be followed. You can try again from the submission details page.'
      );
    }

    if (duplicateIds.length === 1) {
      localStorage.setItem(CRM_ISSUE_ID, String(duplicateIds[0]));
      this.props.history.push(buildIssueUrl(duplicateIds[0], crmToken));
    } else {
      this.props.history.push(
        `${PrivateSubmissionAppRoutes.LISTING}?${PRIVATE_SUBMISSION_LISTING_TAB_QUERY_KEY}=${PRIVATE_SUBMISSION_LISTING_TAB_FOLLOWING_VALUE}`
      );
    }
  };

  handleFollowAndSkip = async (duplicateIds: number[]): Promise<void> => {
    const {
      authContext: { isAuthenticated, triggerLoginChoice },
    } = this.props;
    trackPublicMapFollowClickedDuplicateSkip(isAuthenticated, this.state.duplicateCandidates.length);

    this.setState({ isDuplicateModalOpen: false });

    if (!isAuthenticated) {
      localStorage.setItem(PENDING_FOLLOW_ISSUE_IDS, JSON.stringify(duplicateIds));
      triggerLoginChoice();

      return;
    }

    this.setState({ loading: true });
    await this.executeFollowAndSkip(duplicateIds);
  };

  handleUnfollowDuplicateFromModal = async (duplicateId: number): Promise<void> => {
    const crmToken = getCRMToken();
    if (!crmToken) {
      this.props.addError('Unable to unfollow. Please try again.');

      return;
    }

    try {
      const { followerCount } = await unfollowPublicMapIssue(crmToken, duplicateId);
      this.setState((prev) => ({
        duplicateCandidates: prev.duplicateCandidates.map((d) =>
          d.id === duplicateId ? { ...d, isFollowedByContact: false, followerCount } : d
        ),
      }));
    } catch (err) {
      console.error('[PrivateSubmissionForm] Failed to unfollow duplicate', duplicateId, err);
      this.props.addError('Failed to unfollow. Please try again.');
    }
  };

  handleSubmit = () => {
    if (this.isAddDisabled()) {
      const blockerMessage = this.getAttachmentUploadBlockerMessage();
      if (blockerMessage) {
        this.props.addError(blockerMessage);
      }

      return;
    }

    const {
      authContext: { isAuthenticated, triggerLoginChoice },
    } = this.props;

    trackRequestSubmitted(
      this.state.category?.name || '',
      this.props.userContext?.user?.name,
      this.props.userContext?.user?.email,
      isAuthenticated
    );

    if (!isAuthenticated) {
      const submissionRequestData = this.getIssueSubmissionRequest();
      localStorage.setItem(SUBMISSION_REQUEST_DATA, JSON.stringify(submissionRequestData));
      if (!this.state.isAnonymous) {
        triggerLoginChoice();
      } else {
        // For anonymous submissions, use the CRM token from the URL
        const urlParams = new URLSearchParams(window.location.search);
        const crmToken = urlParams.get('crm_token');
        if (crmToken) {
          this.setState({ loading: true });
          this.props
            .submitIssue(crmToken, submissionRequestData)
            .then((action) => {
              if (action.type === SUBMIT_ISSUE_SUCCESS) {
                this.completeSubmissionSuccess(action.response.id);
                trackRequestCreated(
                  this.state.category?.name || '',
                  this.props.userContext?.user?.name,
                  this.props.userContext?.user?.email,
                  isAuthenticated
                );
              } else {
                localStorage.removeItem(SUBMISSION_REQUEST_DATA);
                this.setState({ loading: false });
                this.props.addError(SUBMIT_REQUEST_FAILED_MESSAGE);
              }
            })
            .catch((error) => {
              localStorage.removeItem(SUBMISSION_REQUEST_DATA);
              this.setState({ loading: false });
              this.props.addError(SUBMIT_REQUEST_FAILED_MESSAGE);
              console.error('Error submitting issue:', error);
            });
        } else {
          localStorage.removeItem(SUBMISSION_REQUEST_DATA);
          this.props.addError('Invalid submission token. Please try again.');
        }
      }
    } else {
      this.submitIssue();
    }
  };

  submitIssue = () => {
    if (this.isAddDisabled()) {
      const blockerMessage = this.getAttachmentUploadBlockerMessage();
      if (blockerMessage) {
        this.props.addError(blockerMessage);
      }

      return;
    }

    const {
      authContext: { isAuthenticated },
    } = this.props;

    this.setState({ loading: true });
    const submissionRequestData = this.getIssueSubmissionRequest(isAuthenticated);
    const crmToken = getCRMToken();
    this.props
      .submitIssue(crmToken, submissionRequestData)
      .then((action) => {
        if (action.type === SUBMIT_ISSUE_SUCCESS) {
          this.completeSubmissionSuccess(action.response.id);
        } else {
          this.setState({ loading: false });
          this.props.addError(SUBMIT_REQUEST_FAILED_MESSAGE);
        }
        trackRequestCreated(
          this.state.category?.name || '',
          this.props.userContext?.user?.name,
          this.props.userContext?.user?.email,
          isAuthenticated
        );
      })
      .catch((error) => {
        this.setState({ loading: false });
        this.props.addError(SUBMIT_REQUEST_FAILED_MESSAGE);
        console.error('Error submitting issue:', error);
      });
  };

  onDescriptionSubmit = () => {
    if (this.getLastStep() === PrivateSubmissionFormSteps.CategoryDetails) {
      this.handleSubmitWithDuplicateCheck();
    }
  };

  onDragEnter = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  onDragExit = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  onDragLeave = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  onDragOver = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.handleAttachmentChange(e);
  };

  getFileEmptyErrorState = () => ({
    file: getEmptyErrorState(),
  });

  getValidFiles = (files: FileList) => {
    let validFiles = [];
    let hasInvalidFiles = false;

    for (const index in files) {
      if (!isNaN(+index)) {
        const splitText = files[index].name.split('.');
        const fileType = `.${splitText[splitText.length - 1]}`;

        if (this.props.acceptedFileTypes.includes(fileType.toLowerCase())) {
          validFiles = validFiles.concat(files[index]);
        } else {
          hasInvalidFiles = true;
        }
      }
    }

    if (hasInvalidFiles && this.props.addError) {
      this.props.addError('One/more files with unsupported format is/are discarded.');
    }

    return validFiles;
  };

  hasPendingAttachmentUploads = () =>
    Array.from(this.state.files.values()).some((file) => this.isAttachmentUploadPending(file));

  isAddDisabled = () => this.hasPendingAttachmentUploads();

  shouldShowSubmitLoader = () => this.hasPendingAttachmentUploads();

  getAttachmentUploadBlockerMessage = () => {
    if (this.hasPendingAttachmentUploads()) {
      return 'Please wait for attachments to finish uploading before submitting.';
    }

    return '';
  };

  handleLocationChange = (location: ILocation) => {
    this.setState({ location });
  };

  handleMarkerChange = (mapCenter: IMapCoordinate) => {
    this.setState({ markerCenter: mapCenter });
  };

  handleAttachmentChange = async (e) => {
    let files = null;
    if (e.dataTransfer) {
      files = this.getValidFiles(e.dataTransfer.files);
    } else if (e.target) {
      files = this.getValidFiles(e.target.files);
    }

    try {
      files = await Promise.all(files.map(FileUtil.convertHeicToJpeg));
    } catch {
      if (this.props.addError) {
        this.props.addError('Could not convert HEIC/HEIF file. Please convert to JPEG and try again.');
      }

      return;
    }

    const isImage = (file) => {
      const split = file.name.split('.');
      const extension = split[split.length - 1];

      return /png|jpe?g/.test(extension.toLowerCase());
    };

    const onUploadProgress = (progressEvent, key) => {
      const progress: number = progressEvent.total ? (progressEvent.loaded / progressEvent.total) * 100 : 0;

      const newFiles = new Map(this.state.files);

      if (newFiles.get(key)) {
        const currentFile = newFiles.get(key);

        newFiles.set(key, {
          ...currentFile,
          progress,
        });

        this.setState({
          files: newFiles,
        });
      }
    };

    const onUploadComplete = (response, key) => {
      const newFiles = new Map(this.state.files);
      const s3SavedUrl = getS3URL(response);

      if (newFiles.get(key) && s3SavedUrl) {
        const currentFile = newFiles.get(key);

        newFiles.set(key, {
          ...currentFile,
          progress: 100,
          s3SavedUrl,
        });

        this.setState({
          files: newFiles,
        });
      } else {
        onUploadFailed(new Error('S3 upload response did not include a saved file location.'), key);
      }
    };

    const onUploadFailed = (error, key) => {
      const newFiles = new Map(this.state.files);

      if (newFiles.get(key)) {
        const currentFile = newFiles.get(key);

        newFiles.set(key, {
          ...currentFile,
          progress: 100,
          s3SavedUrl: null,
          error: {
            ...currentFile.error,
            file: {
              errorState: IValidationState.ERROR,
              errorMessage: ATTACHMENT_UPLOAD_FAILED_MESSAGE,
            },
          },
        });

        this.setState({
          files: newFiles,
        });

        if (this.props.addError) {
          this.props.addError(ATTACHMENT_UPLOAD_FAILED_MESSAGE);
        }
      }
    };

    files.map((file) => {
      const reader = new FileReader();
      const fileKey = getIndexedFileName(file.name);

      reader.onloadend = () => {
        const newFiles = new Map(this.state.files);
        newFiles.set(fileKey, {
          data: reader.result as string,
          details: file,
          progress: 0,
          s3SavedUrl: '',
          error: this.getFileEmptyErrorState(),
        });

        this.props.getCRMSignedURL(getCRMToken(), this.props.s3Operation, fileKey).then((action) => {
          if (action.type === S3_CRM_SIGNED_URL_SUCCESS) {
            const body = new Map(action.response.requestBodyparams);

            if (isImage(file)) {
              // tslint:disable-next-line:no-unused-expression
              new Compressor(file, {
                quality: 0.7,
                success(compressedFile: Blob) {
                  s3uploadFile(body, compressedFile, (ev) => onUploadProgress(ev, fileKey)).then(
                    (response) => {
                      onUploadComplete(response, fileKey);
                    },
                    (error) => {
                      onUploadFailed(error, fileKey);
                    }
                  );
                },
              });
            } else {
              s3uploadFile(body, file, (ev) => onUploadProgress(ev, fileKey)).then(
                (response) => {
                  onUploadComplete(response, fileKey);
                },
                (error) => {
                  onUploadFailed(error, fileKey);
                }
              );
            }
          } else {
            onUploadFailed(action.error, fileKey);
          }
        });

        this.setState({
          files: newFiles,
        });
      };

      if (file.size < this.props.maxFileSize) {
        if (isImage(file)) {
          // tslint:disable-next-line:no-unused-expression
          new Compressor(file, {
            quality: 0.7,
            success: (compressedFile) => {
              reader.readAsDataURL(compressedFile);
            },
          });
        } else {
          reader.readAsDataURL(file);
        }
      } else {
        if (this.props.addError) {
          const maxFileSizeInMB = this.props.maxFileSize / (1024 * 1024);
          this.props.addError(`File size cannot exceed ${maxFileSizeInMB}MB`);
        }
      }
    });
    e.target.value = null;
  };

  getAcceptedFileTypesString = (): string =>
    this.props.acceptedFileTypes ? this.props.acceptedFileTypes.join(', ') : '';

  getFileType = (file: File) => {
    const fileNameSplit: string[] = file.name.split('.');
    const fileNameExtension: string = fileNameSplit[fileNameSplit.length - 1];

    return /(jpe?g|png)$/.test(fileNameExtension.toLowerCase()) ? 'image' : 'others';
  };

  getThumb = (file: IFileUpload) => {
    const fileTypeToThumb = {
      image: (
        <AttachmentImage
          className={classnames('photos-tile__saved-thumb')}
          src={file.data || file.s3SignedURL}
          alt={`Thumbnail preview of ${file.details?.name || 'uploaded file'}`}
        />
      ),
      others: <div className={classnames('photos-tile__saved-thumb', 'photos-tile__saved-thumb--others')} />,
    };

    return fileTypeToThumb[this.getFileType(file.details)];
  };

  getIssueSummaryThumb = (file: ICRMIssueAttachment) => {
    const fileTypeToThumb = {
      image: (
        <AttachmentImage
          className={classnames('photos-tile__saved-thumb')}
          src={file.fileURL}
          alt={`Thumbnail preview of ${file.fileName || 'attachment'}`}
        />
      ),
      others: <div className={classnames('photos-tile__saved-thumb', 'photos-tile__saved-thumb--others')} />,
    };

    return fileTypeToThumb[getFileType(file)];
  };

  getAttachmentUploadProgress = (file: IFileUpload) => Math.min(100, Math.max(0, Math.round(file.progress || 0)));

  isAttachmentUploadPending = (file: IFileUpload) =>
    file?.error?.file?.errorState !== IValidationState.ERROR && !file.s3SavedUrl;

  renderPhotos = () => (
    <div className={classnames('photos-tile__photos', 'tile-contents')}>
      {this.state.files.size > 0 &&
        Array.from(this.state.files.keys()).map((fileKey, index, map) => {
          const file = this.state.files.get(fileKey);
          const hasError = file?.error?.file?.errorState === IValidationState.ERROR;

          return (
            <div key={index} className={classnames('photos-tile__photo-container')}>
              <div className={classnames('delete-photo')}>
                <button
                  type="button"
                  className={classnames('delete-button')}
                  aria-label="Remove file"
                  tabIndex={0}
                  onClick={() => this.removeFile(fileKey)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      this.removeFile(fileKey);
                    }
                  }}
                >
                  <img src="/assets/close-blue.png" alt="" />
                </button>
              </div>
              <div className={classnames('photos-tile__file-wrapper')}>
                {this.getThumb(file)}
                {this.isAttachmentUploadPending(file) && (
                  <div
                    className="file-upload-progress"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={this.getAttachmentUploadProgress(file)}
                    aria-label={`Upload progress for ${file.details?.name || 'attachment'}`}
                  >
                    <span
                      className="file-upload-progress__bar"
                      style={{ width: `${this.getAttachmentUploadProgress(file)}%` }}
                    />
                  </div>
                )}
                {hasError && (
                  <span role="alert" aria-live="polite" className={classnames('field__error', 'file-upload-error')}>
                    {file.error.file.errorMessage}
                  </span>
                )}
              </div>
            </div>
          );
        })}
    </div>
  );

  onCustomFieldChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const targetName = e.currentTarget.name;
    const targetValue = e.currentTarget.value;

    this.setState((prevState) => ({
      categoryfieldValues: {
        ...prevState.categoryfieldValues,
        [targetName]: {
          ...prevState.categoryfieldValues[targetName],
          value: targetValue,
        },
      },
    }));
  };

  handleAddNewFieldValue = (curr: ICRMSecondaryCustomField) => {
    const categoryFieldValues = this.state.categoryfieldValues;
    const newFieldValue = {
      id: curr._id,
      isRequired: curr.isRequired,
      label: curr.label,
      type: curr.type,
      value: '',
      error: getEmptyErrorState(),
    };
    categoryFieldValues[curr._id] = newFieldValue;
    this.setState((prevState) => ({
      ...prevState,
      categoryFieldValues,
    }));
  };

  removeFieldValue = (curr: ICRMSecondaryCustomField) => {
    const fieldValue = this.state.categoryfieldValues;
    if (fieldValue.hasOwnProperty(curr._id)) {
      delete fieldValue[curr._id];
    }
    this.setState((prevState) => ({
      ...prevState,
      categoryfieldValues: fieldValue,
    }));
  };

  renderSelectedCategory = (selectedCategory: ICRMSecondaryCustomField, value: any) => {
    const { categoryfieldValues } = this.state;
    let displaySecondaryCustomField = false;

    if ('condition' in selectedCategory) {
      const { operator, value: conditionValue } = selectedCategory.condition;
      switch (operator) {
        case 'equals':
          displaySecondaryCustomField = value.toLowerCase() === conditionValue.toLowerCase();
          break;
        default:
          displaySecondaryCustomField =
            (conditionValue.toLowerCase() !== '' && value?.toLowerCase().includes(conditionValue.toLowerCase())) ||
            value.toLowerCase() === operator.toLowerCase();
          break;
      }
    }
    const isValid = selectedCategory.isActive && !selectedCategory.isDeleted;
    if (isValid) {
      const hasField = categoryfieldValues.hasOwnProperty(selectedCategory._id);
      if (displaySecondaryCustomField && !hasField) {
        this.handleAddNewFieldValue(selectedCategory);
      } else if (!displaySecondaryCustomField && hasField) {
        this.removeFieldValue(selectedCategory);
      }
    }

    return (
      displaySecondaryCustomField &&
      selectedCategory.isActive &&
      !selectedCategory.isDeleted && (
        <Input
          key={selectedCategory.label}
          field={{
            isRequired: selectedCategory.isRequired,
            label: selectedCategory.label,
            type: selectedCategory.type,
            options: selectedCategory.options,
            value:
              categoryfieldValues &&
              categoryfieldValues[selectedCategory._id] &&
              categoryfieldValues[selectedCategory._id].value,
          }}
          name={`${selectedCategory._id}`}
          error={
            categoryfieldValues &&
            categoryfieldValues[selectedCategory._id] &&
            categoryfieldValues[selectedCategory._id].error
          }
          onChange={this.onCustomFieldChange}
          widthMD={3}
        />
      )
    );
  };

  renderIssueDescriptionForm = () => {
    const { description, error } = this.state;
    const { categoryfieldValues, category: selectedCategory } = this.state;

    const options = this.props.categories?.map((category) => ({
      ...category,
      label: category.name,
      color: 'white',
    }));

    let filteredSuggestedCategories = [];
    this.props.categories?.forEach((cat) => {
      if (this.state.suggestedCategories.includes(cat.id)) {
        filteredSuggestedCategories.push(cat);
      }
    });

    filteredSuggestedCategories = filteredSuggestedCategories.map((category) => ({
      ...category,
      label: category.name,
      color: 'grey',
    }));

    return (
      <>
        <MultiSectionDropdown
          options={options}
          suggestedOptions={filteredSuggestedCategories}
          handleCategorySelect={this.handleCategorySelection}
          getSuggestedCategories={(crmToken, desc) => this.props.getSuggestedCategories(crmToken, desc)}
          crmToken={getCRMToken()}
          error={!this.state.categoryFilled ? 'The category is required.' : ''}
          selectedCategory={selectedCategory}
          onCategorySelected={this.handleCategoryPickerSelect}
          onCategorySearchNoResults={this.handleCategorySearchNoResults}
        />
        <div className={classnames('issue-detail-tile__body__custom-fields')}>
          {selectedCategory &&
            (selectedCategory.fields || [])
              .filter((field) => field.isActive && !field.isDeleted)
              .map((field: any, index) => (
                <>
                  <Input
                    key={field.label}
                    field={{
                      isRequired: field.isRequired,
                      label: field.label,
                      type: field.type,
                      options: field.options,
                      value:
                        categoryfieldValues && categoryfieldValues[field._id] && categoryfieldValues[field._id].value,
                    }}
                    name={`${field._id}`}
                    error={
                      categoryfieldValues && categoryfieldValues[field._id] && categoryfieldValues[field._id].error
                    }
                    onChange={this.onCustomFieldChange}
                    widthMD={3}
                  />
                  {field?.fields?.map((nestedFields: ICRMSecondaryCustomField) =>
                    this.renderSelectedCategory(nestedFields, categoryfieldValues[field._id].value)
                  )}
                </>
              ))}
        </div>
        <Form className={classnames('crm-submission__form')}>
          <Row>
            <Input
              width={12}
              field={{
                label: 'Description',
                type: CustomFieldType.TEXTAREA,
                value: description,
                isRequired: true,
              }}
              error={error.description}
              name="description"
              onChange={this.handleChange}
            />
          </Row>
          <div className={classnames('field-section')}>
            <div>
              {this.state.files.size > 0 && this.renderPhotos()}
              <div
                onDragEnter={this.onDragEnter}
                onDragExit={this.onDragExit}
                onDragLeave={this.onDragLeave}
                onDragOver={this.onDragOver}
                onDrop={this.onDrop}
                className={classnames('photos__drag-drop-box')}
              >
                {isDesktop() ? (
                  <div className={classnames('desktop-view')}>
                    <span className={classnames('file-upload__cloud-img')} />
                    <span>Drag & Drop or </span>
                    <label htmlFor="file-upload" className={classnames('label-desktop')}>
                      Browse file
                    </label>
                    <input
                      id="file-upload"
                      type="file"
                      onChange={(e) => this.handleAttachmentChange(e)}
                      accept={this.getAcceptedFileTypesString()}
                      multiple={true}
                    />
                  </div>
                ) : (
                  <div className={classnames('mobile-view')}>
                    <label htmlFor="file-upload" className={classnames('label-mobile')}>
                      <span className={classnames('file-upload__add-img')} />
                      <span>Attachments</span>
                    </label>
                    <input
                      id="file-upload"
                      type="file"
                      onChange={(e) => this.handleAttachmentChange(e)}
                      accept={this.getAcceptedFileTypesString()}
                      multiple={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={classnames('field-section attachments-modal__disclaimer')}>
            <span className={classnames('attachments-modal__disclaimer-icon')} />
            <div className={classnames('attachments-modal__disclaimer-msg')}>
              <span>Maximum file size (each file) is 25MB</span>
              <span>{`Accepted file types are ${this.getAcceptedFileTypesString()}.`}</span>
            </div>
          </div>
        </Form>
      </>
    );
  };

  setLocationError = (locationError: IFieldValidation) => {
    this.setState({
      error: { ...this.state.error, location: locationError },
    });
  };

  renderLocationForm = () => (
    <Form className={classnames('crm-submission__form')}>
      <Row>
        <div className={classnames('crm-location-section')}>
          <CRMLocationField
            error={this.state.error.location}
            markerCenter={this.state.markerCenter}
            location={locationUtil.getFullAddressFromLocation(this.state.location, this.props.gisVersion)}
            lat={this.state.location && this.state.location.lat}
            long={this.state.location && this.state.location.long}
            onLocationChange={this.handleLocationChange}
            onMarkerChange={this.handleMarkerChange}
            crmToken={getCRMToken()}
            setLocationError={this.setLocationError}
            isManualAddress={this.state.location && this.state.location.isManualAddress}
          />
          <span className={classnames('field__error')} role="alert" aria-live="polite">
            {this.state.error.location.errorState === IValidationState.ERROR ||
            this.state.error.location.errorState === IValidationState.WARNING
              ? this.state.error.location.errorMessage
              : ''}
          </span>
        </div>
      </Row>
    </Form>
  );

  renderStepTitle = (step: number, title: string) => (
    <div
      className={classnames('private-submission__steps__title', {
        disabled: this.state.activeStep !== step && this.state.activeStep < step,
      })}
    >
      <div className="private-submission__steps__title__number">{step + 1}</div>
      <h2 className="private-submission__steps__title__heading">{title}</h2>
    </div>
  );

  moveToStep = (step: number) => {
    this.setState({ activeStep: step });
  };

  getLastStep = () => {
    const { category } = this.state;

    if (!category) {
      return PrivateSubmissionFormSteps.LocationDetails;
    }

    return category.isLocationIncluded
      ? PrivateSubmissionFormSteps.LocationDetails
      : PrivateSubmissionFormSteps.CategoryDetails;
  };

  shouldShowAnonymousSubmission = (): boolean => {
    const { postAnonymous, isAccountsFeatureFlagEnabled } = this.props;
    const { category } = this.state;

    if (!postAnonymous) {
      return false;
    }

    if (!isAccountsFeatureFlagEnabled) {
      if (!category) {
        return true;
      }

      return category.allowAnonymousSubmission !== false;
    }

    // If no category is selected, default to showing (fallback behavior)
    if (!category) {
      return true;
    }

    // Return true only if category explicitly allows anonymous submissions
    return category.allowAnonymousSubmission === true;
  };

  validateCategoryCustomFieldForm = () => {
    const { categoryfieldValues } = this.state;

    const { fieldValues, isValid } = customFieldUtil.validateCategoryFieldValues(categoryfieldValues);

    this.setState({
      categoryfieldValues: fieldValues,
    });

    return isValid;
  };

  renderAccordionSteps = () => {
    const { activeStep, category } = this.state;

    let buttonText = 'Next';

    if (activeStep === this.getLastStep()) {
      buttonText = 'Submit';
    }

    const steps = [
      {
        title: 'Request Details',
        content: this.renderIssueDescriptionForm(),
        validate: this.validateDescriptionForm,
        onSubmit: this.onDescriptionSubmit,
      },
      ...(category && category.isLocationIncluded
        ? [
            {
              title: 'Location',
              content: this.renderLocationForm(),
              validate: this.validateLocationForm,
              onSubmit: this.handleSubmitWithDuplicateCheck,
            },
          ]
        : []),
    ];
    const { isAuthenticated } = this.props.authContext;

    return (
      <>
        <h1 className="private-submission-header">Submit a request</h1>
        <div className="private-submission__steps">
          {steps.map((step, index) => (
            <div id={`private-submission__steps__step-${index}`} key={`step-${index}`}>
              <Accordion
                key={`accordion-${index}`}
                title={this.renderStepTitle(index, step.title)}
                isDisabled={index > activeStep}
                isExpanded={activeStep === index}
                hideBorder={index === steps.length - 1}
                onClick={() => this.moveToStep(index)}
              >
                {step.content}
                <Row className="private-submission__steps__actions">
                  {this.shouldShowAnonymousSubmission() && activeStep === this.getLastStep() && !isAuthenticated && (
                    <div className="anonymous-toggle-wrapper">
                      <div id="anonymous-toggle-label" className="field__label">
                        Do you want your Submission to be Anonymous?
                      </div>
                      <div className="option-radio-btn" role="radiogroup" aria-labelledby="anonymous-toggle-label">
                        <Button
                          bsStyle={ButtonStyle.PRIMARY}
                          content="No"
                          className={!this.state.isAnonymous ? 'active' : ''}
                          onClick={() => this.setState({ isAnonymous: false })}
                          aria-pressed={!this.state.isAnonymous}
                          aria-label="Submit with your identity (Not Anonymous)"
                        />
                        <Button
                          bsStyle={ButtonStyle.DEFAULT}
                          content="Yes"
                          className={this.state.isAnonymous ? 'active' : ''}
                          onClick={() => this.setState({ isAnonymous: true })}
                          aria-pressed={this.state.isAnonymous}
                          aria-label="Submit anonymously (Anonymous)"
                        />
                      </div>
                    </div>
                  )}
                  <Button
                    bsStyle={ButtonStyle.PRIMARY}
                    disabled={this.isAddDisabled()}
                    showLoader={this.shouldShowSubmitLoader()}
                    content={buttonText}
                    onClick={async () => {
                      if (this.isAddDisabled()) {
                        const blockerMessage = this.getAttachmentUploadBlockerMessage();
                        if (blockerMessage) {
                          this.props.addError(blockerMessage);
                        }

                        return;
                      }

                      if (step.validate && !step.validate()) {
                        return;
                      }
                      if (step.onSubmit) {
                        await step.onSubmit();
                      }

                      if (activeStep === this.getLastStep()) {
                        return;
                      }

                      this.moveToStep(this.state.activeStep + 1);
                    }}
                  />
                </Row>
              </Accordion>
            </div>
          ))}
        </div>
      </>
    );
  };

  renderReviewSummary = () => {
    const { issue } = this.props;

    if (!issue) {
      return null;
    }

    const { isAuthenticated } = this.props.authContext;
    const locationString = issue.location
      ? locationUtil.getFullAddressFromLocation(
          {
            ...issue.location,
            lat: issue.location.latitude,
            long: issue.location.longitude,
          },
          this.props.gisVersion
        )
      : null;
    const coordinates: ISubmissionCoordinates | null = issue.location
      ? {
          latitude: issue.location.latitude,
          longitude: issue.location.longitude,
          userPinLatitude: issue.location.userPinLatitude ?? null,
          userPinLongitude: issue.location.userPinLongitude ?? null,
        }
      : null;
    const backURL = isAuthenticated ? PrivateSubmissionAppRoutes.LISTING : null;

    return (
      <PrivateSubmissionDetails
        submission={{ ...issue, coordinates } as unknown as ICustomerSubmissionResponse}
        location={locationString}
        backURL={backURL}
      />
    );
  };

  render() {
    return (
      <PrivateSubmissionLayout>
        {this.state.showSuccessToast && (
          <div className="user-profile__alert user-profile__alert--success" role="status" aria-live="polite">
            <div className="alert-content">
              <i className="fa fa-check-circle" aria-hidden="true"></i>
              <p>Your submission was successfully created.</p>
            </div>
            <button
              type="button"
              onClick={() => this.setState({ showSuccessToast: false })}
              className="close-btn"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        )}
        {this.state.loading ? (
          <Loading show />
        ) : this.props.issue ? (
          this.renderReviewSummary()
        ) : (
          this.renderAccordionSteps()
        )}
        {this.state.isCheckingDuplicates && <DuplicateCheckLoader />}
        <DuplicateDetectionModal
          show={this.state.isDuplicateModalOpen}
          duplicates={this.state.duplicateCandidates}
          onSubmitAnyway={this.handleDuplicateSubmitAnyway}
          onClose={this.handleDuplicateModalClose}
          citizenDuplicateOverrideFlow={this.props.presentDuplicateSubmissionsToCitizens}
          onFollowAndSkip={this.handleFollowAndSkip}
          onUnfollowDuplicate={this.handleUnfollowDuplicateFromModal}
        />
      </PrivateSubmissionLayout>
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  issue: state.crm.issue,
  categories:
    state.crm &&
    state.crm.config &&
    state.crm.config.categories?.filter(
      (category) => category.categoryType === CategoryType.EXTERNAL && category.isActive
    ),
  isSMSEnabled: state.crm && state.crm.config && state.crm.config.isSMSEnabled,
  postAnonymous: state.crm && state.crm.config && state.crm.config.postAnonymous,
  anonymousSubmission: state.crm && state.crm.anonymousSubmission,
  contactPreferenceNote: state.crm.config && state.crm.config.contactPreferenceNote,
  gisVersion: state.productLayout.productLayout?.agency?.agencyInfo?.gisVersion,
  isAccountsFeatureFlagEnabled: isAccountsFeatureFlagEnabled(state),
  presentDuplicateSubmissionsToCitizens: state.crm?.config?.presentDuplicateSubmissionsToCitizens ?? false,
});

const mapDispatchToProps = (dispatch: any) => ({
  validateCRMToken: (crmToken: string) => dispatch(validateCRMToken(crmToken)),
  saveCRMAnonymousIssueDetails: (crmToken: string, anonymousId: string, issueDetails: ICRMIssueAnonymousDetails) =>
    dispatch(saveCRMAnonymousIssueDetails(crmToken, anonymousId, issueDetails)),
  getCRMAnonymousIssueDetails: (crmToken: string, anonymousId: string) =>
    dispatch(getCRMAnonymousIssueDetails(crmToken, anonymousId)),
  submitIssue: (crmToken: string, issueRequest: ICRMIssueSubmissionRequest) =>
    dispatch(submitIssue(crmToken, issueRequest)),
  getCRMSignedURL: (crmToken: string, operation: S3Operation, fileName: string) =>
    dispatch(getCRMSignedURL(crmToken, operation, fileName)),
  getSuggestedCategories: (crmToken: string, description: string) =>
    dispatch(getCRMSuggestedCategories(crmToken, description)),
  getDuplicateIssues: (crmToken: string, params: ICRMDuplicateIssueRequest) =>
    dispatch(getDuplicateIssues(crmToken, params)),
  addError: (errorMessage: string) => dispatch(addError(errorMessage)),
  getCRMConfig: (crmToken: string) => dispatch(getCRMMapConfig(crmToken)),
  getSsoToken: (crmToken: string, authCode: string) => dispatch(getSSOToken(crmToken, authCode)),
  getSsoConfig: (crmToken: string) => dispatch(getSSOConfig(crmToken)),
  getSsoUserInfo: (crmToken: string, idpUserInfoRequest: ISsoUserInfoRequest) =>
    dispatch(getSSOUserInfo(crmToken, idpUserInfoRequest)),
  clearIssue: () => dispatch(clearIssue()),
});

// Export the wrapped component...
export default withUserAndAuthContext(connect(mapStateToProps, mapDispatchToProps)(PrivateSubmissionForm));
